import React, { FunctionComponent, useEffect, useState } from 'react'
import {
  App,
  Button,
  Divider,
  Modal,
  Popconfirm,
  Space,
  type TransferProps,
  Typography
} from 'antd'
import { AssettrakAccess } from 'server'
import AccessDetails from './AccessDetails/AccessDetails'
import { DeleteOutlined, RetweetOutlined, SaveOutlined } from '@ant-design/icons'
import { useFeathersClient } from '@strac/react-client-feathers'
import AccessManagerTableTransfer from './AccessManagerTableTransfer/AccessManagerTableTransfer'
import { useLoaderData, useMatch } from '@tanstack/react-router'

interface IProps {
  assettrakAccess: AssettrakAccess
  open: boolean
  setOpen: (open: boolean) => void
}

const AccessManagerModal: FunctionComponent<any> = (props: IProps) => {
  const { open, setOpen } = props

  const { notification } = App.useApp()

  const client = useFeathersClient()

  const match = useMatch({ strict: false })
  const { agencies, assettrakAccess } = useLoaderData({ from: match.routeId ?? '/' })

  const [loading, setLoading] = useState<boolean>(false)

  const [targetKeys, setTargetKeys] = useState<TransferProps['targetKeys']>([])
  const [selectedKeys, setSelectedKeys] = useState<TransferProps['targetKeys']>([])

  const agencyIds = agencies?.map((a: any) => a.id) || []

  useEffect(() => {
    const agencyIds = agencies?.map((a: any) => a.id) || []
    setTargetKeys(agencyIds)
  }, [agencies])

  const handleSave = () => {
    setLoading(true)
    const allowedAgenciesString = JSON.stringify(targetKeys).replace('[', '').replace(']', '')
    client
      .service('assettrak-access')
      .patch(assettrakAccess.id, {
        allowed_agencies: allowedAgenciesString
      })
      .then((response: any) => {
        notification.success({
          message: 'Success',
          description: 'Access code saved successfully.'
        })
      })
      .catch((e: any) => {
        console.error(e)
        notification.error({
          message: 'Error',
          description: 'There was an error saving the access code. Please try again.'
        })
      })
      .finally(() => {
        setLoading(false)
      })
    setLoading(false)
  }

  const handleReset = () => {
    setTargetKeys(agencyIds)
  }

  const handleDelete = () => {
    client
      .service('assettrak-access')
      .patch(assettrakAccess.id, {
        active: false
      })
      .then((response: any) => {
        notification.success({
          message: 'Success',
          description: 'Access code retired.'
        })
      })
      .catch((e: any) => {
        console.error(e)
        notification.error({
          message: 'Error',
          description: 'There was an error saving the access code. Please try again.'
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Modal
      width={'90vw'}
      destroyOnClose={true}
      footer={null}
      open={open}
      onCancel={() => setOpen(false)}
      centered={true}
      key={assettrakAccess.id}
      title={
        <Typography.Title level={3} style={{ margin: '10px' }}>
          {assettrakAccess.access_code}
        </Typography.Title>
      }
    >
      <AccessDetails assettrakAccess={assettrakAccess} />
      <Divider />
      <AccessManagerTableTransfer
        targetKeys={targetKeys}
        setTargetKeys={setTargetKeys}
        selectedKeys={selectedKeys}
        setSelectedKeys={setSelectedKeys}
      />
      <Divider />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Popconfirm title={'Are you sure you want to retire access code?'} onConfirm={handleDelete}>
          <Button danger={true} ghost={true} type={'primary'} icon={<DeleteOutlined />} size={'large'}>
            Retire Access Code
          </Button>
        </Popconfirm>
        <Space>
          <Button
            onClick={handleReset}
            type={'primary'}
            ghost={true}
            icon={<RetweetOutlined />}
            size={'large'}
          >
            Reset Selection
          </Button>
          <Button onClick={handleSave} type={'primary'} icon={<SaveOutlined />} size={'large'} loading={loading}>
            Save
          </Button>
        </Space>
      </div>
    </Modal>
  )
}

export default AccessManagerModal
